import * as yup from "yup";
import {
  MULTIMEDIA_MIME_TYPES,
  MULTIMEDIA_PLACEHOLDER,
  FILE_ALREADY_EXISTS,
} from "../../../../../utils/constants";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import Textbox from "../../../../../components/common/Textbox";
import ReactSelect from "../../../../../components/common/ReactSelect";
import Datepicker from "../../../../../components/common/Datepicker";
import FileUpload from "../../../../../components/common/FileUpload";
import Toaster from "./../../../../../components/common/Toaster";
import complianceService from "../../../../../services/complianceService";
import moment from "moment";

const schema = yup.object({
  account_compliance_id: yup.string(),
  transaction_id: yup.string(),
  date: yup.string().required("Please select date."),
  show_validity: yup.bool().required().nullable(),
  valid_from: yup.string().when("show_validity", {
    is: (value) => value,
    then: yup.string().required("Please select start date."),
    otherwise: yup.string().notRequired(),
  }),
  valid_to: yup.string().when("show_validity", {
    is: (value) => value,
    then: yup.string().required("Please select end date."),
    otherwise: yup.string().notRequired(),
  }),
  status: yup.object().shape({
    label: yup.string().required("Please select status."),
    value: yup.string().required("Please select status."),
  }),
  files: yup.mixed(),
});

const ComplianceForm = ({ id, data, isLoading, setLoading, onSuccess }) => {
  const [showValidity, setShowValidity] = useState(false);
  const [fileExists, setFileExists] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [statuses, setStatuses] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await complianceService.saveComplianceStatusAsync(id, data);
      Toaster.Success("Compliance Status Saved Successfully");
      onSuccess();
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
      } else {
        Toaster.Error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    const value = status.value;

    if (data.is_recurring)
      if (value === 1) {
        setValue("show_validity", true);
        setShowValidity(true);
        return;
      }

    setValue("show_validity", false);
    setShowValidity(false);
  };

  const handleStartDate = (value) => {
    const formatted_date = moment(value, moment.ISO_8601).format("YYYY-MM-DD");
    setStartDate(formatted_date);
  };

  const validDate = (current) => {
    return current.isAfter(startDate);
  };

  useEffect(() => {
    const fetchStatuses = () => {
      const data = complianceService.getComplianceStatuses();
      setStatuses(data);
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    if (data) setValue("account_compliance_id", data.account_compliance_id);
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="transaction-id-textbox"
            name="transaction_id"
            type="text"
            title="Transaction No."
            register={register("transaction_id")}
            errors={errors}
            optional
          />
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-1">
          <ReactSelect
            id="status-dropdown"
            name="status"
            title="Status"
            options={statuses}
            control={control}
            onSelectChange={handleStatusChange}
            register={register("status")}
            errors={errors}
          />
        </div>
        <div className="col-span-1">
          <Datepicker
            id="date-datepicker"
            name="date"
            title="Date"
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            onDateChange={handleStartDate}
            register={register("date")}
            timeFormat={false}
            errors={errors}
            readOnly
          />
        </div>
      </div>
      {showValidity && (
        <div className="grid grid-cols-2 space-x-4">
          <div className="col-span-1">
            <Datepicker
              id="valid-from-datepicker"
              name="valid_from"
              title="Valid From"
              placeholder="MM-DD-YYYY"
              dateFormat="MM-DD-yyyy"
              setValue={setValue}
              clearErrors={clearErrors}
              onDateChange={handleStartDate}
              register={register("valid_from")}
              timeFormat={false}
              errors={errors}
              readOnly
            />
          </div>
          <div className="col-span-1">
            <Datepicker
              id="valid-to-datepicker"
              name="valid_to"
              title="Valid To"
              placeholder="MM-DD-YYYY"
              dateFormat="MM-DD-yyyy"
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("valid_to")}
              isValidDate={validDate}
              timeFormat={false}
              errors={errors}
              readOnly
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <FileUpload
            id="files-dropdown"
            name="files"
            title="Files"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("files")}
            errors={errors}
            accepts={MULTIMEDIA_MIME_TYPES}
            placeholder={MULTIMEDIA_PLACEHOLDER}
            maxFiles={2}
            maxSize={5242880}
            multiple={true}
          />
          {fileExists && (
            <div className="pt-2">
              <span className="text-sm text-red-400">
                {FILE_ALREADY_EXISTS}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default ComplianceForm;
